import React from 'react';
import CustomTooltip from "../CustomTooltip";
import {IconChipDot} from "../buttons/IconButtons";

/**
 * @fileoverview A reusable React component that displays a truncated text cell with optional tooltip support.
 * It detects overflowed text and conditionally wraps the content with a MUI Tooltip if enabled.
 * Commonly used in tables or compact UI grids where horizontal space is limited.
 *
 * @module components/EllipsisCell
 * @author Ferro Zapata Arnulfo
 * @email azapata@ibl.mx
 * @jobTitle Full Stack Developer
 */


/**
 * EllipsisCell Component
 *
 * @param {Object} props - Component props.
 * @param {string|number|React.ReactNode} props.value - The raw value to be displayed in the cell.
 * @param {function(value: any): React.ReactNode} [props.render] - Optional render function to customize display of the value.
 * @param {boolean|string|React.ReactNode} [props.tooltip=false] - If true, shows full value in tooltip; if string or node, uses it as tooltip content.
 * @param {'bottom'|'top'|'left'|'right'} [props.placement='top'] - Tooltip placement direction.
 * @param {string} [props.className='w-100'] - Optional Tailwind or Bootstrap width class for the container.
 * @param {string} [props.classNameContainerTooltip='']  Additional classes for the container that wraps both tooltipped elements.
 * @param {string} [props.classNameTooltip=''] Extra classes applied specifically to the main text inside tooltip.
 * @param {string} [props.classNameSubTextTooltip=''] Extra classes applied to the subtext tooltip.
 * @returns {JSX.Element} A cell with optional ellipsis and tooltip.
 *
 */

const EllipsisCell = ({ value, render, tooltip = false, placement = 'top',className = 'w-100',
                          classNameContainerTooltip='', classNameTooltip='', classNameSubTextTooltip='' }) => {
    const aux = render ? render(value) : value;
    const content = aux?.text || aux;
    const icon = aux?.icon;
    const subText = aux?.subText;
    const subChip = aux?.subChip;

    return (
        <div className={`${className}`}>
            { tooltip ?
                <>
                    <div className={classNameContainerTooltip}>
                        <div className={'minw-0'}>
                            <CustomTooltip
                                text={content}
                                classes={`text-ellipsis-1-row ${classNameTooltip}`}
                                showIfTruncated={true}
                                placement={placement}
                                icon={icon}
                            />
                        </div>

                        {subText && <>
                            <div><IconChipDot/></div>
                            <div className={'minw-0'}>
                                <CustomTooltip
                                    text={subText}
                                    classes={`text-ellipsis-1-row ${classNameSubTextTooltip}`}
                                    showIfTruncated={true}
                                    placement={placement}
                                />
                            </div>
                        </>}
                    </div>
                    {subChip && subChip}
                </>
                :
                content
            }
        </div>
    );
};

export default EllipsisCell;
